import { ApiError, apiErrorFromResponse } from '@/api/errors'
import type { AuthSession } from '@/api/types'

export type QueryValue = string | number | boolean | null | undefined
export type QueryParams = Record<string, QueryValue | QueryValue[]>

export interface RequestOptions {
  query?: QueryParams
  body?: unknown
  signal?: AbortSignal
  headers?: Record<string, string>
  /** `false` pour les routes publiques (login, aperçu d'invitation). */
  auth?: boolean
}

export interface ApiClientOptions {
  baseUrl: string
  /** Jeton d'accès courant, ou `null` si la session est anonyme. */
  getAccessToken: () => string | null
  /** Jeton de rafraîchissement courant. */
  getRefreshToken: () => string | null
  /** Appelé après un rafraîchissement réussi, avec la nouvelle session. */
  onSessionRefreshed: (session: AuthSession) => void
  /** Appelé quand le rafraîchissement échoue : la session doit être vidée. */
  onAuthenticationLost: () => void
  fetchImpl?: typeof fetch
}

/** Sérialise les paramètres de requête en ignorant `undefined` et `null`. */
export function buildQueryString(query: QueryParams | undefined): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    const values = Array.isArray(value) ? value : [value]
    for (const item of values) {
      if (item === undefined || item === null || item === '') continue
      params.append(key, String(item))
    }
  }
  const serialized = params.toString()
  return serialized ? `?${serialized}` : ''
}

const REFRESH_PATH = '/auth/refresh'

export class ApiClient {
  private readonly options: ApiClientOptions
  private readonly fetchImpl: typeof fetch
  /** Rafraîchissement en cours : toutes les requêtes en 401 attendent la même promesse. */
  private refreshPromise: Promise<AuthSession> | null = null

  constructor(options: ApiClientOptions) {
    this.options = options
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis)
  }

  get baseUrl(): string {
    return this.options.baseUrl
  }

  async request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
    const response = await this.send(method, path, options)

    if (response.status === 401 && (options.auth ?? true) && path !== REFRESH_PATH) {
      const refreshed = await this.tryRefresh()
      if (refreshed) {
        // Rejeu unique : un second 401 avec un jeton frais signifie que la
        // session est réellement perdue (utilisateur désactivé, jeton révoqué).
        const retried = await this.send(method, path, options)
        if (retried.status === 401) this.options.onAuthenticationLost()
        return this.parse<T>(retried)
      }
      this.options.onAuthenticationLost()
    }

    return this.parse<T>(response)
  }

  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, options)
  }

  /**
   * Réponse **texte** : l'export Markdown du cahier des charges (lot 12) rend
   * `text/markdown`, que `parse` rendrait `undefined` faute de JSON. Même
   * rafraîchissement de session que le reste.
   */
  async getText(path: string, options: RequestOptions = {}): Promise<string> {
    const withAccept = { ...options, headers: { Accept: 'text/markdown', ...options.headers } }
    let response = await this.send('GET', path, withAccept)

    if (response.status === 401 && (options.auth ?? true)) {
      if (await this.tryRefresh()) {
        response = await this.send('GET', path, withAccept)
        if (response.status === 401) this.options.onAuthenticationLost()
      } else {
        this.options.onAuthenticationLost()
      }
    }

    if (!response.ok) throw await apiErrorFromResponse(response)
    return response.text()
  }

  /**
   * Réponse **binaire** (lot 38b) : l'audio d'une étape (`audio/mpeg`), le
   * paquet d'une formation (`application/zip`). Une balise `<audio src>` ne
   * porte pas le jeton ; on lit le flux ici. Un refus garde son motif (409
   * `training_audio_incomplete` et ses étapes), comme le reste du client.
   */
  async getBlob(
    path: string,
    options: RequestOptions = {},
  ): Promise<{ blob: Blob; disposition: string | null }> {
    const withAccept = { ...options, headers: { Accept: '*/*', ...options.headers } }
    let response = await this.send('GET', path, withAccept)

    if (response.status === 401 && (options.auth ?? true)) {
      if (await this.tryRefresh()) {
        response = await this.send('GET', path, withAccept)
        if (response.status === 401) this.options.onAuthenticationLost()
      } else {
        this.options.onAuthenticationLost()
      }
    }

    if (!response.ok) throw await apiErrorFromResponse(response)
    return { blob: await response.blob(), disposition: response.headers.get('Content-Disposition') }
  }

  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, { ...options, body })
  }

  /**
   * `POST` à réponse **texte** : l'aperçu d'un courriel (lot 16) rend
   * `text/html`, que `parse` rendrait `undefined`. Même rafraîchissement de
   * session que `getText`.
   */
  async postText(path: string, body: unknown, options: RequestOptions = {}): Promise<string> {
    const withAccept = {
      ...options,
      body,
      headers: { Accept: 'text/html', ...options.headers },
    }
    let response = await this.send('POST', path, withAccept)

    if (response.status === 401 && (options.auth ?? true)) {
      if (await this.tryRefresh()) {
        response = await this.send('POST', path, withAccept)
        if (response.status === 401) this.options.onAuthenticationLost()
      } else {
        this.options.onAuthenticationLost()
      }
    }

    if (!response.ok) throw await apiErrorFromResponse(response)
    return response.text()
  }

  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, { ...options, body })
  }

  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, { ...options, body })
  }

  delete<T = void>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, options)
  }

  private async send(method: string, path: string, options: RequestOptions): Promise<Response> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-OO-Client': 'web',
      ...options.headers,
    }

    if (options.auth ?? true) {
      const token = this.options.getAccessToken()
      if (token) headers.Authorization = `Bearer ${token}`
    }

    const hasBody = options.body !== undefined
    /**
     * Un `FormData` part **tel quel** : c'est le navigateur qui pose
     * `multipart/form-data` avec sa frontière, qu'on ne sait pas écrire à la
     * main. Le sérialiser en JSON enverrait `{}` — un fichier perdu en
     * silence. Passer par ici plutôt que par un `XMLHttpRequest` garde le
     * rafraîchissement de session, que l'envoi avec progression perd.
     */
    const isForm = hasBody && options.body instanceof FormData
    if (hasBody && !isForm) headers['Content-Type'] = 'application/json'

    const url = `${this.options.baseUrl}${path}${buildQueryString(options.query)}`

    try {
      return await this.fetchImpl(url, {
        method,
        headers,
        body: isForm
          ? (options.body as FormData)
          : hasBody
            ? JSON.stringify(options.body)
            : undefined,
        signal: options.signal,
      })
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      throw new ApiError(
        0,
        'network_error',
        'Impossible de se connecter. Vérifiez votre connexion.',
      )
    }
  }

  /** Rafraîchit la session au plus une fois à la fois ; `false` si c'est impossible. */
  private async tryRefresh(): Promise<boolean> {
    const refreshToken = this.options.getRefreshToken()
    if (!refreshToken) return false

    // La promesse est capturée localement : un autre appelant peut remettre le
    // champ à `null` entre-temps sans que celui-ci n'attende `null`.
    const pending = (this.refreshPromise ??= this.performRefresh(refreshToken))

    try {
      await pending
      return true
    } catch {
      return false
    } finally {
      if (this.refreshPromise === pending) this.refreshPromise = null
    }
  }

  private async performRefresh(refreshToken: string): Promise<AuthSession> {
    const response = await this.send('POST', REFRESH_PATH, {
      body: { refresh_token: refreshToken },
      auth: false,
    })
    if (!response.ok) throw await apiErrorFromResponse(response)
    const session = (await response.json()) as AuthSession
    // Notifié ici — donc une seule fois — quel que soit le nombre d'appelants
    // en attente de cette même promesse.
    this.options.onSessionRefreshed(session)
    return session
  }

  private async parse<T>(response: Response): Promise<T> {
    if (!response.ok) throw await apiErrorFromResponse(response)
    if (response.status === 204) return undefined as T

    const contentType = response.headers.get('content-type') ?? ''
    if (!contentType.includes('application/json')) return undefined as T

    const text = await response.text()
    if (!text) return undefined as T
    return JSON.parse(text) as T
  }
}

/**
 * Base d'API : `VITE_API_BASE_URL`, sans barre oblique finale.
 *
 * Par défaut **relative** : le navigateur appelle l'hôte d'où vient la page,
 * quelle que soit l'adresse à laquelle on l'a ouverte. En production, nginx
 * sert `/api` ; en développement, Vite le relaie (voir `vite.config.ts`).
 * Poser `VITE_API_BASE_URL` reste possible pour viser une API ailleurs, mais
 * l'adresse écrite est alors la seule joignable depuis toutes les machines.
 */
export function resolveBaseUrl(raw: string | undefined): string {
  const value = raw?.trim() ? raw.trim() : '/api/v1'
  return value.replace(/\/+$/, '')
}
